import { DomainError } from '../errors.js';

/** Granular permissions (TZ section 9). Deny by default: absence is refusal. */
export const PERMISSIONS = [
  'sites.read',
  'sites.create',
  'sites.deploy',
  'sites.destroy',
  'backups.read',
  'backups.create',
  'backups.restore',
  'backups.restore_production',
  'backups.delete',
  'nodes.read',
  'nodes.enroll',
  'nodes.manage',
  'domains.read',
  'domains.manage',
  // Buying a domain spends money (ADR-0039).
  'domains.purchase',
  'templates.read',
  'templates.manage',
  // Articles a site gets written and published (ADR-0051): the work of an editor, not of infrastructure.
  'content.manage',
  // Tags are labels on sites, not infrastructure. Anyone who may create a site
  // may label it; nothing is reachable through a tag that was not reachable
  // without one.
  'tags.manage',
  'secrets.manage',
  'jobs.read',
  'jobs.cancel',
  'audit.read',
  'team.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLES = ['owner', 'admin', 'operator', 'editor', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

const READ_ONLY: readonly Permission[] = [
  'sites.read',
  'backups.read',
  'nodes.read',
  'domains.read',
  'templates.read',
  'jobs.read',
];

const OPERATOR: readonly Permission[] = [
  ...READ_ONLY,
  'sites.create',
  'sites.deploy',
  'backups.create',
  'backups.restore',
  'domains.manage',
  'tags.manage',
  'content.manage',
  'jobs.cancel',
];

const ADMIN: readonly Permission[] = [
  ...OPERATOR,
  'sites.destroy',
  'backups.restore_production',
  'backups.delete',
  'nodes.enroll',
  'nodes.manage',
  'templates.manage',
  'audit.read',
];

export const ROLE_PERMISSIONS: Record<Role, ReadonlySet<Permission>> = {
  owner: new Set(PERMISSIONS),
  admin: new Set([...ADMIN, 'secrets.manage', 'team.manage', 'domains.purchase']),
  operator: new Set(OPERATOR),
  // Editor works inside a site's own CMS, so it needs to find the site and its articles, and nothing else.
  editor: new Set<Permission>(['sites.read', 'jobs.read', 'content.manage']),
  viewer: new Set(READ_ONLY),
};

export function permissionsFor(roles: readonly Role[]): ReadonlySet<Permission> {
  const granted = new Set<Permission>();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role]) granted.add(permission);
  }
  return granted;
}

export function hasPermission(roles: readonly Role[], permission: Permission): boolean {
  return roles.some((role) => ROLE_PERMISSIONS[role].has(permission));
}

/**
 * Called on the HTTP edge and again in the worker before destructive execution
 * (TZ section 13). The second check is not redundant: a job can sit in the queue
 * long enough for the actor to lose the role that queued it.
 */
export function assertPermission(roles: readonly Role[], permission: Permission): void {
  if (!hasPermission(roles, permission)) {
    throw new DomainError('FORBIDDEN', `Missing permission ${permission}`, { permission });
  }
}

/** Roles that must carry MFA (TZ section 13). */
export const MFA_REQUIRED_ROLES: readonly Role[] = ['owner', 'admin'];

export function requiresMfa(roles: readonly Role[]): boolean {
  return roles.some((role) => MFA_REQUIRED_ROLES.includes(role));
}
